import { useCallback, useMemo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { throttle } from 'throttle-debounce';

import { cx } from '@libs/classnames';
import { ContentEditableText } from '@ui/components/ContentEditableText/ContentEditableText';
import { DragHandle } from '@ui/components/DragHandle/DragHandle';
import { DragHandleWidget } from '@ui/components/DragHandle/DragHandleWidget';
import { TableCell, TableRow } from '@ui/components/Table';
import { RemoveIcon } from '@ui/icons/RemoveIcon';

import { ThemeIconButton } from '@features/shared/ui/ThemeIconButton';

import styles from '../TournamentParticipantsEdit.module.css';

type Props = {
  className?: string;
  num: number;
  name: string;
  teamId: string;
  onBan?: (teamId: string) => void;
  onDeleteTeam?: (teamId: string) => void;
  onRename?: (teamId: string, name: string) => void;
  sortDisabled?: boolean;
  disabled?: boolean;
  isRcf: boolean;
  isSchoolEvent: boolean;
  showNumbers: boolean;
};

export function TeamRow({
  className,
  num,
  name,
  teamId,
  onBan,
  onDeleteTeam,
  onRename,
  sortDisabled,
  disabled,
  isRcf,
  isSchoolEvent,
  showNumbers,
}: Props) {
  const { setNodeRef, transition, transform, active } = useSortable({
    id: teamId,
    data: {
      type: 'team',
      teamId,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: active?.id === teamId ? 0 : undefined,
  };

  const handleDelete = useMemo(() => {
    if (!onDeleteTeam) return undefined;
    return () => onDeleteTeam(teamId);
  }, [teamId, onDeleteTeam]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleRename = useCallback(
    throttle(
      1000,
      (name: string) => {
        if (!name) return;
        onRename?.(teamId, name);
      },
      { noLeading: true },
    ),
    [onRename, teamId],
  );

  const handleBan = useMemo(() => {
    if (!onBan) return undefined;
    return () => {
      const accepted = confirm(`Забанить команду ${name} в турнире?`);
      if (accepted) {
        onBan(teamId);
      }
    };
  }, [teamId, onBan, name]);

  let placeholderCellColSpan = 1;
  if (isRcf) placeholderCellColSpan++;
  if (isSchoolEvent) placeholderCellColSpan++;

  return (
    <TableRow
      className={cx(styles.teamRow, { [styles.disabled]: disabled }, className)}
      hoverable
      style={style}
      ref={setNodeRef}
    >
      <TableCell slim>{!sortDisabled && <DragHandleWidget id={teamId} />}</TableCell>
      {showNumbers && <TableCell className={styles.numbers}>{num}</TableCell>}
      <TableCell colSpan={2}>
        {onRename ? (
          <ContentEditableText
            className={styles.isClickable}
            initialValue={name}
            onCommit={handleRename}
            onBlur={handleRename}
          />
        ) : (
          name
        )}
      </TableCell>
      <TableCell right colSpan={placeholderCellColSpan}></TableCell>

      <TableCell right>
        {handleBan && (
          <ThemeIconButton className={styles.banButton} onClick={handleBan}>
            Бан
          </ThemeIconButton>
        )}
        {handleDelete && (
          <ThemeIconButton onClick={handleDelete}>
            <RemoveIcon />
          </ThemeIconButton>
        )}
      </TableCell>
    </TableRow>
  );
}

type TeamRowPreviewProps = {
  className?: string;
  num: number;
  name: string;
  isRcf: boolean;
  isSchoolEvent: boolean;
};
export function TeamRowPreview({
  className,
  num,
  name,
  isRcf,
  isSchoolEvent,
}: TeamRowPreviewProps) {
  let placeholderCellColSpan = 1;
  if (isRcf) placeholderCellColSpan++;
  if (isSchoolEvent) placeholderCellColSpan++;
  return (
    <TableRow className={cx(styles.teamRow, className)}>
      <TableCell slim>
        <DragHandle />
      </TableCell>
      <TableCell className={styles.numbers}>{num}</TableCell>
      <TableCell colSpan={2}>{name}</TableCell>
      <TableCell colSpan={placeholderCellColSpan} right></TableCell>
      <TableCell right />
    </TableRow>
  );
}
